import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { TableFilter, useTableFilterContext } from 'features/TableFilter';
import { WarehouseSelect } from '../WarehouseSelect';
import { ZonesListFilterKeys } from '../../model/types';
import { useGetAllWarehousesQuery } from 'entities/Warehouse';

export const ZonesListFilter: FC = memo(() => {
  const { t } = useAppTranslation(['common']);

  const { filters, changeFilters, clearCurrentFilters } = useTableFilterContext<ZonesListFilterKeys>();
  const { warehouseId } = useParams();

  const { data: warehouses } = useGetAllWarehousesQuery();

  const currentWarehouseLabel = useMemo(() => {
    if (!warehouses || !warehouseId) {
      return '';
    }

    return warehouses.find((warehouse) => warehouse.warehouseId === warehouseId)?.name || warehouseId;
  }, [warehouses, warehouseId]);

  useEffect(() => {
    if (!warehouseId) {
      return;
    }

    if (filters.selectedWarehouseId?.value === warehouseId) {
      return;
    }

    changeFilters({
      key: ZonesListFilterKeys.selectedWarehouseId,
      value: warehouseId,
      label: currentWarehouseLabel,
      title: t('Warehouse'),
    });
  }, [warehouseId, currentWarehouseLabel, changeFilters, t, filters.selectedWarehouseId?.value]);

  const selectWarehouse = useCallback(
    (value: string, label: string) => {
      changeFilters({
        key: ZonesListFilterKeys.selectedWarehouseId,
        label,
        value,
        title: t('Warehouse'),
      });
    },
    [changeFilters, t],
  );

  return (
    <TableFilter>
      <div className="space-y-2">
        <WarehouseSelect value={filters.selectedWarehouseId?.value} onChange={selectWarehouse} onClear={clearCurrentFilters} />
      </div>
    </TableFilter>
  );
});
